/**
 * The support ticket this conversation became, or the one press that makes it
 * one.
 *
 * One control with two states rather than two controls: a thread either has a
 * ticket or it does not, and the header has no room to keep a button for the
 * half of the question that is currently false. Once a ticket exists the circle
 * turns into a link to it and stays filled, which is the same "this is set"
 * vocabulary the client circle next to it uses.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { convertToTicket, type MailThread } from "../../../../lib/mail";
import { IconBtn, useToast } from "../../../ui";

export function TicketButton({ thread, onChanged }: { thread: MailThread; onChanged: () => void }) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (thread.ticketId) {
    return (
      <Link
        to={`/support/${thread.ticketId}`}
        className="crm-cb is-on"
        title={t("email.viewTicket")}
        aria-label={t("email.viewTicket")}
      >
        <LifeBuoy className="size-4" />
      </Link>
    );
  }

  async function convert() {
    setBusy(true);
    try {
      await convertToTicket(thread.id);
      onChanged();
    } catch {
      // The strip this button came from swallowed the failure: the spinner
      // stopped, no ticket appeared, and nothing said why.
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <IconBtn
      onClick={() => void convert()}
      disabled={busy}
      title={t("email.convertTicket")}
      aria-label={t("email.convertTicket")}
    >
      {busy ? <Loader2 className="size-4 animate-spin" /> : <LifeBuoy className="size-4" />}
    </IconBtn>
  );
}
